import { types as api } from '@mesa-labs/mesa-api';
import { VerificationProcess } from '@mesa-labs/mesa-api/dist/types';
import { ApexOptions } from 'apexcharts';
import { Decimal } from 'decimal.js';
import { DateTime } from 'luxon';
import { Dispatch, SetStateAction } from 'react';
import { StylesConfig } from 'react-select';

export const USER_TIMEZONE_LOCAL_STORAGE_KEY = 'userTimezone';

export const asExportFilter = <T>(filter: T): T => ({
  ...filter,
  page: 1,
  limit: Number.MAX_SAFE_INTEGER,
});

export type PoNumberMatchType = 'begins_with' | 'ends_with' | 'contains' | 'equals';

export const PO_NUMBER_MATCH_TYPE_OPTIONS: { label: string; value: PoNumberMatchType }[] = [
  { label: 'Begins With', value: 'begins_with' },
  { label: 'Ends With', value: 'ends_with' },
  { label: 'Contains', value: 'contains' },
  { label: 'Equals', value: 'equals' },
];

export function buildPoNumberPattern(matchType: PoNumberMatchType, value: string): string {
  const escaped = value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  switch (matchType) {
    case 'begins_with': return `^${escaped}`;
    case 'ends_with': return `${escaped}$`;
    case 'contains': return escaped;
    case 'equals': return `^${escaped}$`;
  }
}

export const capitalize = (
  str = ' ',
): string => str.trim().split(' ').map((s: string) => (s
  ? s[0].toUpperCase() + s.slice(1).toLowerCase()
  : ''
)).join(' ');

export const snakeCaseToProperCase = (str: string): string => capitalize(str.replace(/_/g, ' '));
export const kebabCaseToProperCase = (str: string): string => capitalize(str.replace(/-/g, ' '));
export const camelCaseToProperCase = (str: string): string => {
  const [first, ...rest] = str.split(/(?<=[a-z])(?=[A-Z])/);
  return [first[0].toUpperCase() + first.slice(1), ...rest].join(' ');
};

const COLLECTED_OR_SETTLED_STATES: string[] = [
  api.InvoiceState.COLLECTED,
  api.InvoiceState.PARTIALLY_COLLECTED,
  api.InvoiceState.SETTLED,
  api.InvoiceState.PARTIALLY_SETTLED,
  api.InvoiceState.OVER_SETTLED,
];

export function computeDaysSince(
  startDate: string | undefined,
  state: string | undefined,
  collectedAt: string | undefined,
  settledAt: string | undefined,
): number | undefined {
  if (!startDate) return undefined;
  const isCollectedOrSettled = state !== undefined && COLLECTED_OR_SETTLED_STATES.includes(state);
  const endDate = isCollectedOrSettled && (collectedAt || settledAt)
    ? DateTime.fromISO((collectedAt || settledAt)!)
    : DateTime.now();
  return endDate.startOf('day').diff(DateTime.fromISO(startDate).startOf('day'), 'days').days;
}

// "3 days ago" style, abbreviated ("3d ago").
export const formatRelativeTime = (iso: string): string => DateTime.fromISO(iso).toRelative({ style: 'short' }) ?? '';

// "10/2/2026, 3:04 PM" in the viewer's locale.
export const formatDateTime = (iso: string): string => DateTime.fromISO(iso).toLocaleString(DateTime.DATETIME_SHORT);

// "Oct 2" in the viewer's locale.
export const formatMonthDay = (iso: string): string => DateTime.fromISO(iso).toLocaleString({ month: 'short', day: 'numeric' });

// "10/2", for tight spaces.
export const formatNumericMonthDay = (iso: string): string => DateTime.fromISO(iso).toFormat('M/d');

// "10/02/26".
export const formatShortDate = (iso: string): string => DateTime.fromISO(iso).toFormat('MM/dd/yy');

export const cleanPrice = (
  price: string,
): string => price.replace(/[^0-9.]+/g, '');

export const toPrice = (
  price: number,
): string => `$${price.toFixed(2)}`;

export const formatPhoneNumber = (
  value: string,
  previousValue: string,
  keyCode: string,
): string => {
  // only allow 0-9 input
  const currentValue = value.replace(/[^\d]/g, '');
  const cvLength = currentValue.length;

  if (!value) {
    return currentValue;
  }

  // on backspace, keep the formatting
  if (keyCode === 'Backspace') {
    return currentValue.slice(0, previousValue.length - 1);
  }

  if (!previousValue || value.length > previousValue.length) {
    if (cvLength < 4) {
      return currentValue;
    }

    if (cvLength < 7) {
      return `(${currentValue.slice(0, 3)}) ${currentValue.slice(3)}`;
    }

    return `(${currentValue.slice(0, 3)}) ${currentValue.slice(3, 6)}-${currentValue.slice(6, 10)}`;
  }

  return currentValue;
};

export const formatCurrency = (amount: number, currency: api.CurrencyCode, maximumFractionDigits = 2) => Intl.NumberFormat(undefined, currency ? { style: 'currency', currency, maximumFractionDigits } : {}).format(amount);

export const wait = (ms: number): Promise<void> => new Promise(
  (resolve) => { setTimeout(resolve, ms); },
);

export const waitAtLeast = async (fn: () => any, ms: number): Promise<any> => {
  const start = Date.now();
  const result = await fn();
  const end = Date.now();
  const time = end - start;

  if (time < ms) {
    await wait(ms - time);
  }

  return result;
};

export const orderObject = (
  obj: Record<string, any>,
  order: string[],
): Record<string, any> => order.reduce((accum: Record<string, any>, curr: string) => ({
  ...accum,
  [curr]: obj[curr],
}), {});

export const randomNumber = (max = 10000000): number => Math.random() * max;

export const randomInt = (max = 10000000): number => Math.floor(randomNumber(max));

export const paginationPrev = (state: number, dispatch: Dispatch<SetStateAction<number>>): () => void => () => {
  if (state - 1 < 1) {
    return;
  }

  dispatch(state - 1);
};

export const paginationNext = (state: number, total: number, dispatch: Dispatch<SetStateAction<number>>): () => void => () => {
  if (state + 1 > total) {
    return;
  }

  dispatch(state + 1);
};

export const orderBy = (
  order: string[],
  collection: Record<string, any>,
): Record<string, any> => order.reduce((accum, key) => ({
  ...accum,
  [key]: collection[key],
}), {});

export function hashCode(str: string): number {
  let hash = 0;
  if (str.length === 0) {
    return hash;
  }
  for (let i = 0; i < str.length; i += 1) {
    const char = str.charCodeAt(i);
    // eslint-disable-next-line no-bitwise
    hash = ((hash << 5) - hash) + char;
    // eslint-disable-next-line no-bitwise
    hash &= hash; // Convert to 32bit integer
  }
  return hash;
}

const COLOR_ASSET = 'rgba(138, 243, 255, 1)';
const COLOR_LIABILITY = 'rgba(255, 154, 59, 1)';
const COLOR_REVENUE = 'rgba(53, 245, 32, 1)';
const COLOR_EXPENSE = 'rgba(255, 23, 23, 1)';
const COLOR_EQUITY = 'rgba(255, 215, 0, 1)';

const COLOR_MESA = 'rgba(7, 16, 88, 0.5)';
const COLOR_JLL = 'rgba(255, 134, 102, 0.5)';
export const COLOR_VENDOR = 'rgba(64, 84, 237, 0.5)';
export const COLOR_CLIENT = 'rgba(0, 154, 117, 0.5)';
const COLOR_CREDIT_FACILITY = 'rgba(254, 200, 75, 0.5)';
const COLOR_SOFTWARE_SERVICE_PROVIDER = 'rgba(252, 52, 0, 0.5)';

export const accountCategoryStyling = (accountCategory: string): string | undefined => {
  switch (accountCategory) {
    case 'Asset':
      return COLOR_ASSET;
    case 'Liability':
      return COLOR_LIABILITY;
    case 'Revenue':
      return COLOR_REVENUE;
    case 'Expense':
      return COLOR_EXPENSE;
    case 'Equity':
      return COLOR_EQUITY;
    default:
      return undefined;
  }
};

export const HashToHexColor = (str: string): string => {
  let hash = 0, i, chr;
  if (str.length === 0) return `#000000`;
  for (i = 0; i < str.length; i++) {
    chr = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return `#${hash.toString(16).substr(0, 6)}`;
}

export const accountCategoryRowStyling = (row: Record<string, unknown>): string | undefined => accountCategoryStyling(row.accountCategory as string);

export const accountCategoryColorById = (id: number): string | undefined => {
  if (!id) {
    return undefined;
  }

  if (id >= 100000 && id < 200000) {
    return COLOR_ASSET;
  }
  if (id >= 200000 && id < 300000) {
    return COLOR_LIABILITY;
  }

  if (id >= 300000 && id < 400000) {
    return COLOR_REVENUE;
  }

  if (id >= 400000 && id < 500000) {
    return COLOR_EXPENSE;
  }

  if (id >= 500000 && id < 600000) {
    return COLOR_EQUITY;
  }

  return undefined;
};

export const businessTypeRowStyling = (row: Record<string, unknown>): string | undefined => {
  const type = row.businessType;
  switch (type) {
    case 'Mesa':
      return COLOR_MESA;
    case 'JLL':
      return COLOR_JLL;
    case 'Vendor':
      return COLOR_VENDOR;
    case 'Client':
      return COLOR_CLIENT;
    default:
      return undefined;
  }
};

export const businessTypeColorById = (id: number): string | undefined => {
  if (!id) {
    return undefined;
  }

  if (id === 1) {
    return COLOR_MESA;
  }
  if (id === 2) {
    return COLOR_JLL;
  }
  if (id === 3) {
    return COLOR_VENDOR;
  }
  if (id === 4) {
    return COLOR_CLIENT;
  }
  if (id === 5) {
    return COLOR_CREDIT_FACILITY;
  }
  if (id === 6) {
    return COLOR_SOFTWARE_SERVICE_PROVIDER;
  }

  return undefined;
};

export const accountCategoryByAccountIdRowStyling = (row: Record<string, unknown>): string | undefined => accountCategoryColorById(row.accountId as number);

const cssBusinessType = (styles: any, id: number) => {
  const colorRgba = businessTypeColorById(id);
  return {
    ...styles,
    borderRadius: '4px',
    marginBottom: '4px',
    backgroundColor: colorRgba,
  };
};

const cssAccountCategory = (styles: any, id: number) => {
  const colorRgba = accountCategoryColorById(id);
  return {
    ...styles,
    borderRadius: '4px',
    marginBottom: '4px',
    backgroundColor: colorRgba,
  };
};

export enum UsTimezone {
  Pacific = 'America/Los_Angeles',
  Mountain = 'America/Denver',
  Central = 'America/Chicago',
  Eastern = 'America/New_York',
}

export const useTimezoneSelection = (dateTime: DateTime, keepLocalTime?: boolean): DateTime => {
  const item = localStorage.getItem(USER_TIMEZONE_LOCAL_STORAGE_KEY);
  const userTimezone = item ? JSON.parse(item) : undefined;
  return userTimezone ? dateTime.setZone(userTimezone.value, keepLocalTime !== undefined ? { keepLocalTime } : undefined) : dateTime.setZone(UsTimezone.Pacific, keepLocalTime !== undefined ? { keepLocalTime } : undefined);
};

export const businessDimensionStyle: StylesConfig<any> = {
  singleValue: (styles, { data }) => cssBusinessType(styles, data.value.type as number),
  option: (styles, { data }) => cssBusinessType(styles, data.value.type as number),
};

export const businessTypeStyle: StylesConfig<any> = {
  singleValue: (styles, { data }) => cssBusinessType(styles, data.value as number),
  option: (styles, { data }) => cssBusinessType(styles, data.value as number),
};

export const accountCategoryStyle: StylesConfig<any> = {
  singleValue: (styles, { data }) => cssAccountCategory(styles, data.value),
  option: (styles, { data }) => cssAccountCategory(styles, data.value),
};

export const toFilterDimension = (dimensions: readonly api.DimensionResponse[]): Array<any> => dimensions.map((businessTypeDimension) => ({
  label: businessTypeDimension.name,
  value: businessTypeDimension.id,
}));

export const stringSeriesToFilterDimension = (stringSeries: string[]): Array<any> => stringSeries.map((s) => ({
  label: s,
  value: s,
}));

export type Selection<T = any> = {
  readonly label: string;
  readonly value: T;
};

export const enumEntryToSelection = <T extends string | number = any>(label: string, entry: any): Selection<T> => ({
  label,
  value: entry,
});

export const enumValueToSelection = <T extends string | number | undefined = any>(entry: any): Selection<T> => ({
  label: snakeCaseToProperCase(entry.toString()),
  value: entry,
});

export const findSelectionByValueOrDefault = <T extends string | undefined>(value: T, selections: Array<Selection<T | undefined>>, defaultSelection?: Selection<T | undefined>): Selection<T | undefined> => selections.find((s) => s.value === value) || defaultSelection || selections[0];

export const toFilterEnum = <T extends string>(enumObj: Record<string, T>): Array<Selection<T>> => Object.entries(enumObj)
  .map((x) => enumEntryToSelection(snakeCaseToProperCase(x[0]), x[1]));

export const toFilterEnumFromNumberEnum = <T extends number>(enumObj: Record<string, T | string>): Array<Selection<T>> => Object.entries(enumObj)
  .filter((x) => !Number.isNaN(parseInt(x[1].toString(), 10)))
  .map((x) => enumEntryToSelection<T>(snakeCaseToProperCase(x[0]), x[1] as T));

export const toStartOfDayLocalTime = (jsDate: Date): Date => useTimezoneSelection(DateTime.fromJSDate(jsDate)).startOf('day').toJSDate();

export const toEndOfDayLocalTime = (jsDate: Date): Date => useTimezoneSelection(DateTime.fromJSDate(jsDate)).endOf('day').toJSDate();

export const toEndOfWeekLocalTime = (jsDate: Date): Date => useTimezoneSelection(DateTime.fromJSDate(jsDate)).endOf('week').toJSDate();


export enum TimeSeriesPointAnchoring {
  BeginningAt,
  EndingAt
}

export const runningStatisticsSeriesDifference = (x: api.RunningStatistic, y: api.RunningStatistic): api.RunningStatistic => {
  return { timeSeries: x.timeSeries.map((pt, idx) => ({ valueDuringPeriod: pt.valueDuringPeriod - y.timeSeries[idx].valueDuringPeriod, totalAtEndOfPeriod: pt.totalAtEndOfPeriod - y.timeSeries[idx].totalAtEndOfPeriod, beginningAt: pt.beginningAt, endingAt: pt.endingAt })) };
}

export const runningStatisticsSeriesRatio = (x: api.RunningStatistic, y: api.RunningStatistic): api.RunningStatistic => {
  return {
    timeSeries: x.timeSeries.map((pt, idx) => ({
      valueDuringPeriod: isFinite(pt.valueDuringPeriod / y.timeSeries[idx].valueDuringPeriod) ? pt.valueDuringPeriod / y.timeSeries[idx].valueDuringPeriod : 1,
      totalAtEndOfPeriod: isFinite(pt.totalAtEndOfPeriod / y.timeSeries[idx].totalAtEndOfPeriod) ? pt.totalAtEndOfPeriod / y.timeSeries[idx].totalAtEndOfPeriod : 1,
      beginningAt: pt.beginningAt,
      endingAt: pt.endingAt
    }))
  };
}

export const runningStatisticToCumulativeXyData = (runningStatistic: api.RunningStatistic, anchoring: TimeSeriesPointAnchoring = TimeSeriesPointAnchoring.EndingAt): any[] => (runningStatistic?.timeSeries || []).map((pt) => ({
  x: DateTime.fromISO(anchoring === TimeSeriesPointAnchoring.BeginningAt ? pt.beginningAt : pt.endingAt)
    .toJSDate(),
  y: pt.totalAtEndOfPeriod,
}));

export const runningStatisticToInterPeriodGrowthXyData = (runningStatistic: api.RunningStatistic, anchoring: TimeSeriesPointAnchoring = TimeSeriesPointAnchoring.EndingAt): any[] => (runningStatistic?.timeSeries || []).map((pt) => ({
  x: DateTime.fromISO(anchoring === TimeSeriesPointAnchoring.BeginningAt ? pt.beginningAt : pt.endingAt)
    .toJSDate(),
  y: pt.valueDuringPeriod,
}));

export const runningStatisticToBurnup = (series: api.TimeSeriesPoint[], prevTotal: number): any[] => (series)
  // Don't include future datapoints in burnup, since it does not apply to projections
  .filter((pt) => DateTime.now() > DateTime.fromISO(pt.beginningAt))
  .map((pt) => ({
    x: useTimezoneSelection(DateTime.fromISO(pt.endingAt)).day,
    y: pt.totalAtEndOfPeriod - prevTotal,
    markedAsFriday: useTimezoneSelection(DateTime.fromISO(pt.endingAt)).weekday === 5
  }));

export const runningStatisticFromCalculationXyData = (statistics: readonly api.RunningStatistic[], calcFn: (pts: readonly api.TimeSeriesPoint[], i: number, statistics: readonly api.RunningStatistic[]) => any): any[] => (statistics[0]?.timeSeries || []).map((pt, i) => {
  const pts = statistics.map((s) => s.timeSeries[i]);
  return {
    x: DateTime.fromISO(pt.beginningAt)
      .toJSDate(),
    y: calcFn(pts, i, statistics),
  };
});

export const DefaultChartColors = [
  '#375DFB' // Mesa blue
];

export const REFERENCE_LINE_COLORS: Record<number, string> = {
  1: '#f59e0b',
  2: '#10b981',
  3: '#3b82f6',
  6: '#8b5cf6',
  12: '#000000',
  // Day-based equivalents (30d = 1mo, 60d = 2mo, 90d = 3mo, 180d = 6mo, 360d = 12mo)
  30: '#f59e0b',
  60: '#10b981',
  90: '#3b82f6',
  180: '#8b5cf6',
  360: '#000000',
};

export const boxPlotOptions: ApexOptions = {
  chart: {
    zoom: {
      allowMouseWheelZoom: false,
    },
  },
  plotOptions: {
    bar: {
      horizontal: true,
      barHeight: '50%',
      borderRadius: 4,
    },
    boxPlot: {
      colors: {
        upper: '#e9ecef',
        lower: '#f8f9fa',
      },
    },
  },
  yaxis: {
    labels: {
      minWidth: 200,
      maxWidth: 300,
      align: 'left',
      style: {
        fontSize: '16px',
      },
    },
  },
  stroke: {
    colors: ['#6c757d'],
  },
};

export const IdentityFormatter = (val: number) => val.toString();
export const IdentityFormatterFromString = (val: string) => val;
export const IdentityFormatterWithEmptyZeroes = (val: number) => val === 0 ? '' : val.toString();

export const CurrencyFormatter = (num: number) => formatCurrency(num, api.CurrencyCode.USD);
export const CurrencyFormatterFromString = (numStr: string) => CurrencyFormatter(parseFloat(numStr)).toString()
export const RoundingFormatter = (places = 2) => (val: number) => val.toFixed(places);

export const PercentageFormatter = (num: number): string => ((num || num === 0) ? Number(num).toLocaleString(undefined, { style: 'percent', minimumFractionDigits: 2 }) : '');
export const PercentageFormatterFromString = (num: string): string => ((num || num === '0') ? Number(num).toLocaleString(undefined, { style: 'percent', minimumFractionDigits: 2 }) : '');

export const lineChartOptions = (formatter: (val: number, opts?: any) => string | string[] = IdentityFormatter, colors?: Array<any>, annotations: ApexAnnotations = { yaxis: [], xaxis: [] }, showMarkers = false): ApexOptions => ({
  annotations,
  colors,
  chart: {
    animations: { enabled: false },
    height: 350,
    width: '100%',
    type: 'line',
    zoom: {
      allowMouseWheelZoom: false,
    },
  },
  dataLabels: { enabled: false },
  markers: showMarkers ? {
    size: 5,
    shape: 'circle',
    strokeWidth: 2,
    strokeColors: colors,
    colors: ['transparent'],
    hover: {
      sizeOffset: 3,
    },
  } : {
    size: 0,
    hover: {
      sizeOffset: 6,
    },
  },
  xaxis: {
    type: 'datetime',
    labels: {
      style: {
        fontSize: '16px',
      },
      format: 'M/d/yy',
      datetimeFormatter: {
        year: 'yyyy',
        month: 'yy\'MMM',
        day: 'MMM dd',
        hour: 'HH:mm',
      },
    },
  },
  yaxis: {
    labels: {
      formatter,
    },
  },
  grid: {
    borderColor: '#f1f1f1',
  },
});

export const categoryLineChartOptions = (formatter: (val: number, opts?: any) => string | string[] = IdentityFormatter, colors?: Array<any>, annotations: ApexAnnotations = { yaxis: [], xaxis: [] }): ApexOptions => ({
  annotations,
  colors,
  chart: {
    animations: { enabled: false },
    height: 350,
    width: '100%',
    type: 'line',
    zoom: {
      allowMouseWheelZoom: false,
    },
  },
  dataLabels: { enabled: false },
  markers: {
    size: 0,
    hover: {
      sizeOffset: 6,
    },
  },
  xaxis: {
    type: 'category',
  },
  yaxis: {
    labels: {
      formatter,
    },
  },
  grid: {
    borderColor: '#f1f1f1',
  },
});

export const outstandingBalanceChartOptions = (formatter: (val: number, opts?: any) => string | string[] = CurrencyFormatter): ApexOptions => ({
  ...lineChartOptions(formatter),
  stroke: {
    curve: 'stepline',
    width: 2,
  },
});

export const outstandingBalanceChartSeries = (response: api.OutstandingBalanceTimeseriesResponse): ApexAxisChartSeries =>
  response.series.map((series) => ({
    name: series.label,
    data: series.points.map((point) => [DateTime.fromISO(point.date).toMillis(), point.amount]),
  }));

// Response is shaped per-entity (one series per vendor/buyer/partner/total/other), each carrying both
// fundedAmount and collectedAmount per week -- this always queries groupBy TOTAL, so there's exactly
// one entity series to pull the two amounts out of into the desired "Funded"/"Repaid" pair.
export const fundedRepaidChartSeries = (response: api.FundedRepaidTimeseriesResponse): ApexAxisChartSeries => {
  const points = response.series[0]?.points ?? [];
  return [
    { name: 'Funded', data: points.map((point) => [DateTime.fromISO(point.weekStart).toMillis(), point.fundedAmount]) },
    { name: 'Repaid', data: points.map((point) => [DateTime.fromISO(point.weekStart).toMillis(), point.collectedAmount]) },
  ];
};

type AdditionalChartOptions = {
  toolbar?: boolean;
  markerSize?: number;
}

const AdditonalChartOptionsDefault = {
  toolbar: true,
  markerSize: 5,
};

export const burnupLineChartOptions = (formatter: (val: number, opts?: any) => string | string[] = IdentityFormatter, discrete?: ApexDiscretePoint[], colors?: string[], annotations: ApexAnnotations = { yaxis: [], xaxis: [] },
  additionalOptions: AdditionalChartOptions = AdditonalChartOptionsDefault): ApexOptions => ({
    annotations,
    colors,
    chart: {
      animations: { enabled: false },
      height: 350,
      width: '100%',
      type: 'line',
      zoom: {
        allowMouseWheelZoom: false,
      },
      toolbar: {
        show: additionalOptions.toolbar,
      },
    },
    stroke: {
      curve: 'smooth',
      width: 6,
      lineCap: 'round',
    },
    dataLabels: { enabled: false },
    markers: {
      size: additionalOptions.markerSize,
      discrete,
    },
    xaxis: {
      type: 'numeric',
      labels: {
        formatter: ((value) => parseInt(value).toString()),
        style: {
          fontSize: '16px',
        }
      },
    },
    yaxis: {
      labels: {
        formatter,
      },
    },
    grid: {
      borderColor: '#f1f1f1',
    },
  });

export const heatMapOptions = (formatter: (val: number, opts?: any) => string = CurrencyFormatter): ApexOptions => ({
  chart: {
    zoom: {
      allowMouseWheelZoom: false,
    },
  },
  yaxis: {
    labels: {
      style: {
        fontSize: '14px',
      },
    },
  },
  dataLabels: {
    style: {
      colors: ['#000000'],
    },
    distributed: false,
    formatter,
  },
  colors: ['#00FF00'],
  stroke: {
    colors: ['#000000'],
  },
  fill: {
    colors: ['#000000'],
  },
  plotOptions: {
    heatmap: {
      distributed: false,
      shadeIntensity: 0.5,
      radius: 0,
    },
  },
});

export const barChartOptions = (formatter: (val: number, opts?: any) => string | string[] = IdentityFormatter, colors?: Array<any>, additionalOptions: AdditionalChartOptions = AdditonalChartOptionsDefault): ApexOptions => ({
  colors,
  chart: {
    animations: { enabled: false },
    width: '100%',
    zoom: {
      allowMouseWheelZoom: false,
    },
  },
  plotOptions: {
    bar: {
      borderRadius: 4,
    },
  },
  dataLabels: { enabled: false },
  markers: {
    size: 0,
    hover: {
      sizeOffset: 6,
    },
  },
  xaxis: {
    type: 'datetime',
    labels: {
      style: {
        fontSize: '16px',
      },
      format: 'M/d/yy',
      datetimeFormatter: {
        year: 'yyyy',
        month: 'yy\'MMM',
        day: 'MMM dd',
        hour: 'HH:mm',
      },
    },
  },
  yaxis: {
    labels: {
      formatter,
    },
  },
  grid: {
    borderColor: '#f1f1f1',
  },
});

export const stackedBarChartOptions = ({
  dataLabelsFormatter = CurrencyFormatterFromString,
  yAxisFormatter = CurrencyFormatter,
  colors
}: {
  dataLabelsFormatter?: (val: string, opts?: any) => string;
  yAxisFormatter?: (val: number, opts?: any) => string;
  colors?: Array<any>;
} = {}): ApexOptions => ({
  colors,
  chart: {
    type: 'bar',
    stacked: true,
    animations: { enabled: false },
    width: '100%',
    zoom: {
      allowMouseWheelZoom: false,
    },
  },
  dataLabels: { enabled: false },
  plotOptions: {
    bar: {
      dataLabels: {
        total: {
          formatter: dataLabelsFormatter,
          enabled: true,
          offsetY: 0,
          style: {
            fontSize: '13px',
            fontWeight: 900
          }
        }
      },
      borderRadius: 4,
    },
  },
  markers: {
    size: 0,
    hover: {
      sizeOffset: 6,
    },
  },
  xaxis: {
    type: 'datetime',
    labels: {
      style: {
        fontSize: '16px',
      },
      format: 'M/d/yy',
      datetimeFormatter: {
        year: 'yyyy',
        month: 'yy\'MMM',
        day: 'MMM dd',
        hour: 'HH:mm',
      },
    },
  },
  yaxis: {
    labels: {
      formatter: yAxisFormatter,
    },
  },
  grid: {
    borderColor: '#f1f1f1',
  },
});

export const stackedHorizontalBarChartOptions = (stackType: 'normal' | '100%' = 'normal', dataLabelsFormatter: (val: string, opts?: any) => string = IdentityFormatterFromString, colors?: Array<any>): ApexOptions => ({
  colors,
  chart: {
    type: 'bar',
    stacked: true,
    stackType,
    animations: { enabled: false },
    width: '100%',
    zoom: {
      allowMouseWheelZoom: false,
    },
  },
  dataLabels: { enabled: false },
  plotOptions: {
    bar: {
      horizontal: true,
      dataLabels: {
        total: {
          formatter: dataLabelsFormatter,
          enabled: true,
          offsetY: 0,
          style: {
            fontSize: '13px',
            fontWeight: 900
          }
        }
      },
      borderRadius: 4,
    },
  },
  markers: {
    size: 0,
    hover: {
      sizeOffset: 6,
    },
  },
  grid: {
    borderColor: '#f1f1f1',
  },
});

export const parseOptionalBooleanStr = (optBooleanStr: string | undefined): boolean | undefined => {
  if (optBooleanStr) {
    const cleaned = optBooleanStr.toLowerCase().trim();
    if (cleaned === 'true') {
      return true;
    } else if (cleaned === 'false') {
      return false
    } else {
      return undefined;
    }
  } else {
    return undefined;
  }
}

export function getErrorMessage(error: any, defaultMessage: string): string;
export function getErrorMessage(error?: any, defaultMessage?: string): string | undefined;
export function getErrorMessage(error?: any, defaultMessage?: string): string | undefined {
  return error?.data?.message || error?.message || defaultMessage;
}

export const isNetOrPOAProgram = (programCode: api.ProgramCodes): boolean => {
  return programCode.toString().startsWith('NET') || programCode === api.ProgramCodes.POA;
}

export const parseMaybePercentage = (str: string): number => {
  const value = str.trim();
  if (value.endsWith('%')) {
    return new Decimal(value.substring(0, value.length - 1)).dividedBy(100).toNumber();
  } else {
    return new Decimal(value).toNumber();
  }
}

export const formatPercentage = (value: number, fractionalDigits?: number) => Number(value).toLocaleString(undefined, { style: 'percent', minimumFractionDigits: fractionalDigits || 2, maximumFractionDigits: fractionalDigits || 4 });

export const toPercentage = (value: number) => `${new Decimal(value).times(100).toString()}%`;

export const getTimeSeriesPeriodName = (beginningAt: DateTime, endingAt: DateTime, periodization: api.TimeSeriesPeriodization, isMultiMonth: boolean, isMultiYear: boolean): string => {
  switch (periodization) {
    case api.TimeSeriesPeriodization.DAY:
      return isMultiYear ? beginningAt.toFormat('MM/dd/yy') : isMultiMonth ? beginningAt.toFormat('MM/dd') : beginningAt.toFormat('dd');
    case api.TimeSeriesPeriodization.WEEK:
      return isMultiYear
        ? `${beginningAt.toFormat(`MMM d yy`)} - ${endingAt.toFormat(`MMM d yy`)}`
        : isMultiMonth
          ? `${beginningAt.toFormat(`MMM d`)} - ${endingAt.toFormat(`MMM d`)}` :
          `${beginningAt.toFormat(`d`)} - ${endingAt.toFormat(`d`)}`;
    case api.TimeSeriesPeriodization.MONTH:
      return isMultiYear
        ? `${beginningAt.toFormat('MMM d yy')} - ${endingAt.toFormat('MMM d yy')}`
        : `${beginningAt.toFormat('MMM d')} - ${endingAt.toFormat('MMM d')}`;
    case api.TimeSeriesPeriodization.QUARTER:
      return isMultiYear ? beginningAt.toFormat('Qq yy') : beginningAt.toFormat('Qq')
    case api.TimeSeriesPeriodization.YEAR:
    case api.TimeSeriesPeriodization.CENTURY:
      return beginningAt.toFormat('yyyy');
  }
}

export const appendFilterParams = <T extends object>(url: URL, filterParams: T | undefined) => {
  Object.entries(filterParams || {}).forEach(([k, v]) => {
    if (v !== null && v !== undefined) {
      if (Array.isArray(v)) {
        v.forEach((arrayVal) => {
          if (arrayVal !== null && arrayVal !== undefined) {
            url.searchParams.append(`${k}[]`, arrayVal.toString());
          }
        });
      } else {
        url.searchParams.append(k, v.toString());
      }
    }
  });
}

export const getCollectionProviderName = (provider: string): string => provider.toLowerCase() === api.CollectionProviders.SVB ? provider.toUpperCase() : capitalize(provider);

export function reorderForColumnFirst(
  sortedItems: { label: string; route: string }[],
  columns = 2
): { label: string; route: string }[] {
  const rows = Math.ceil(sortedItems.length / columns);
  const columnsArray: { label: string; route: string }[][] = Array.from(
    { length: columns },
    (_, colIndex) =>
      sortedItems.slice(colIndex * rows, (colIndex + 1) * rows)
  );

  const result: { label: string; route: string }[] = [];
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < columns; col++) {
      if (columnsArray[col][row]) {
        result.push(columnsArray[col][row]);
      }
    }
  }

  return result;
}

export function getHasExpired(expiresAt: string | undefined): boolean {
  return !!expiresAt && DateTime.fromISO(expiresAt) < DateTime.now();
}

export function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}
